import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Loader} from '@flexe/ui-components';
import {CookiesProvider, useCookies} from 'react-cookie';
import {get} from 'lodash';
import WarehouseSelector from '../shared/WarehouseSelector';
import {CsvDataError, GenericUploadError, Response, ResponseError, Warehouse} from '../shared/CommonInterfaces';
import ErrorDisplay from '../shared/ErrorDisplay';
import FlexeButton from '../shared/FlexeButton';
import {UploadParams} from '../shipper/edi-files/EdiFilesInterfaces';
import CsvPostUploadStep from '../shared/CsvPostUploadStep';
import FileDownloadLink from '../shared/FileDownloadLink';
import SpaConfigurationRulesBulkUploadModal from './SpaConfigurationRulesBulkUploadModal';
import SpaConfigurationsRulesService, {SpaRule} from './SpaConfigurationsRulesService';
import {Rule} from './SpaConfigurationsInterfaces';
import {instructionsTableHeaders} from './SpaConfigurationRuleInstructionsData';
import RulesTable from './RulesTable';
import TestRulesModal from './TestRulesModal';

interface Props {
  authenticityToken: string;
  continuationToken: string;
  activeWarehouses: Warehouse[];
  inactiveWarehouses: Warehouse[];
  currentPage: number;
  pageSize: number;
  selectedWarehouse: Warehouse;
  spaConfigRulesService: SpaConfigurationsRulesService;
  displayNewRulesTable: boolean;
  handlePageSizeChange(pageSize);
  handlePageChange(currentPage);

  handleWarehouseChange(selectedWarehouse);

  handleContinuationTokenChange(continuationToken);
}

const mapApiRuleToLocalRule = (rule: SpaRule, index: number): Rule => {
  return {
    id: get(rule, 'priority'),
    reservationId: get(rule, 'reservation_id') == null ? ' ' : get(rule, 'reservation_id').toString(),
    sku: get(rule, 'sku') == null ? ' ' : get(rule, 'sku').toString(),
    packaging: get(rule, 'packaging') == null ? ' ' : get(rule, 'packaging'),
    itemVelocity: get(rule, 'itemVelocity') == null ? ' ' : get(rule, 'itemVelocity'),
    isHazmat: get(rule, 'isHazmat') == null ? ' ' : get(rule, 'isHazmat').toString(),
    isLotCodeTracked: get(rule, 'isLotCodeTracked') == null ? ' ' : get(rule, 'isLotCodeTracked').toString(),
    isExpirationDateTracked:
      get(rule, 'isExpirationDateTracked') == null ? ' ' : get(rule, 'isExpirationDateTracked').toString(),
    isShipAlone: get(rule, 'isShipAlone') == null ? ' ' : get(rule, 'isShipAlone').toString(),
    isShipAsIs: get(rule, 'isShipAsIs') == null ? ' ' : get(rule, 'isShipAsIs').toString(),
    zoneNames: get(rule, 'zoneNames') == null ? ' ' : get(rule, 'zoneNames'),
    routeToLocationCategory: get(rule, 'locationCategories') == null ? ' ' : get(rule, 'locationCategories'),
    routeToLocation: get(rule, 'locationLabels') == null ? ' ' : get(rule, 'locationLabels'),
    csa: get(rule, 'csa')
  };
};

const SpaConfigurationsIndex: React.FC<Props> = ({
  authenticityToken,
  continuationToken,
  activeWarehouses,
  inactiveWarehouses,
  currentPage,
  pageSize,
  selectedWarehouse,
  spaConfigRulesService,
  displayNewRulesTable,
  handlePageSizeChange,
  handlePageChange,
  handleWarehouseChange,
  handleContinuationTokenChange
}) => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [errorResponse, setErrorResponse] = useState<Response | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [fetchingRules, setFetchingRules] = useState<boolean>(false);
  const [totalRulesCount, setTotalRulesCount] = useState<number>(0);
  const [showCsvUploadModal, setShowCsvUploadModal] = useState<boolean>(false);
  const [showTestRulesUploadModal, setShowTestRulesUploadModal] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadComplete, setUploadComplete] = useState<boolean>(false);
  const [responseErrors, setResponseErrors] = useState<ResponseError[]>([]);
  const [genericErrors, setGenericErrors] = useState<GenericUploadError[]>([]);
  const [csvErrors, setCsvErrors] = useState<CsvDataError[]>([]);
  const [instructionTableHeaderRows, setInstructionTableHeaderRows] = useState<React.ReactNode[][]>([]);
  const [requiredHeaders, setRequiredHeaders] = useState<string[][]>([]);
  const [cookies] = useCookies(['selected_warehouse_id']);

  const continuationTokens = useRef<string[]>([]);

  const fetchRules = useCallback(
    async (warehouseId: number, currentPageSize: number, token?: string | null) => {
      setFetchingRules(true);
      setErrorResponse(undefined);
      setErrorMessage('');

      try {
        const response = await spaConfigRulesService.getRules(warehouseId, currentPageSize, token);

        if (response.errors.length > 0) {
          const newErrorMessage = spaConfigRulesService.processErrors(response.errors).join(', ');
          setErrorMessage(newErrorMessage);
          setRules([]);
          setTotalRulesCount(0);
          return;
        }

        const {continuationToken: nextPageToken, rules: spaRules, fullResultSetSize} = response.data;

        if (nextPageToken && currentPage + 1 > continuationTokens.current.length) {
          continuationTokens.current.push(nextPageToken);
        }

        const mappedRules = spaRules ? spaRules.map(mapApiRuleToLocalRule) : [];
        setRules(mappedRules);
        setTotalRulesCount(fullResultSetSize || 0);
      } catch (err) {
        setErrorResponse(err as Response);
      } finally {
        setFetchingRules(false);
      }
    },
    [spaConfigRulesService, currentPage]
  );

  const fetchImportInstructions = useCallback(
    async (warehouseId: number) => {
      try {
        const response = await spaConfigRulesService.fetchImportInstructions(warehouseId);
        const instructions = response.data.import_instructions;

        const newInstructionTableHeaderRows: React.ReactNode[][] = [];
        const newRequiredHeaders: string[][] = [];

        instructions.forEach((header) => {
          const headerRow = [
            header.name,
            header.isRequired,
            header.description,
            header.input_guidelines,
            header.sample_values
          ];
          newInstructionTableHeaderRows.push(headerRow);
          // Ensure isRequired is a string for the string[][] type
          newRequiredHeaders.push([header.name.trim().toUpperCase(), String(header.isRequired)]);
        });

        setInstructionTableHeaderRows(newInstructionTableHeaderRows);
        setRequiredHeaders(newRequiredHeaders);
      } catch (err) {
        setErrorResponse(err as Response);
      }
    },
    [spaConfigRulesService]
  );

  useEffect(() => {
    fetchRules(cookies.selected_warehouse_id, pageSize, continuationToken);
    fetchImportInstructions(cookies.selected_warehouse_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookies.selected_warehouse_id]);

  const resetContinuationTokens = useCallback(() => {
    continuationTokens.current = [];
    handleContinuationTokenChange(null);
  }, [handleContinuationTokenChange]);

  const handleUpload = useCallback(
    async (params: UploadParams, type: string) => {
      setUploading(true);
      const apiParams = {content: params.file, fileType: type};

      const response = await spaConfigRulesService.importRules(apiParams, cookies.selected_warehouse_id);
      setGenericErrors(get(response, 'data.errors.generic_errors', []));
      setCsvErrors(get(response, 'data.errors.csv_errors', []));
      setResponseErrors(response.errors);
      setShowCsvUploadModal(false);
      setUploading(false);
      setUploadComplete(true);

      // Improvement: After upload, reset pagination and refetch the first page of rules.
      resetContinuationTokens();
      await fetchRules(cookies.selected_warehouse_id, pageSize, null);
    },
    [spaConfigRulesService, cookies.selected_warehouse_id, fetchRules, pageSize, resetContinuationTokens]
  );

  const handlePagination = useCallback(
    async (page: number) => {
      let token: string | null = null;
      if (displayNewRulesTable) {
        token = page > 0 ? continuationTokens.current[page - 1] : null;
      } else {
        token = page > 1 ? continuationTokens.current[page - 2] : null;
      }
      handleContinuationTokenChange(token);
      handlePageChange(page);
      await fetchRules(cookies.selected_warehouse_id, pageSize, token);
    },
    [
      displayNewRulesTable,
      handleContinuationTokenChange,
      handlePageChange,
      fetchRules,
      cookies.selected_warehouse_id,
      pageSize
    ]
  );

  const handleWarehouseSelected = useCallback(
    async (newSelectedWarehouse: Warehouse) => {
      const newCurrentPage = displayNewRulesTable ? 0 : 1;
      handlePageChange(newCurrentPage);
      handleWarehouseChange(newSelectedWarehouse);

      resetContinuationTokens();
      // Fetch rules for the new warehouse, starting from the beginning (null token)
      await fetchRules(newSelectedWarehouse.id, pageSize, null);
    },
    [displayNewRulesTable, handlePageChange, handleWarehouseChange, resetContinuationTokens, fetchRules, pageSize]
  );

  const changePageSize = useCallback(
    (newPageSize: number) => {
      const newCurrentPage = displayNewRulesTable ? 0 : 1;
      resetContinuationTokens();
      handlePageSizeChange(newPageSize);
      handlePageChange(newCurrentPage);
      fetchRules(cookies.selected_warehouse_id, newPageSize, null);
    },
    [
      displayNewRulesTable,
      resetContinuationTokens,
      handlePageSizeChange,
      handlePageChange,
      fetchRules,
      cookies.selected_warehouse_id
    ]
  );

  const toggleShowCsvUploadModal = useCallback(() => {
    setShowCsvUploadModal((prev) => !prev);
  }, []);

  const toggleTestRulesUploadModal = useCallback(() => {
    setShowTestRulesUploadModal((prev) => !prev);
  }, []);

  return (
    <div className="container-fluid spa-config-index">
      <ErrorDisplay errorResponse={errorResponse} errorText={errorMessage} />
      <div className="row">
        <div className="col-sm-6">
          <h1> Zones </h1>
        </div>
        <div className={'col-md-6 spa-csv-buttons'}>
          {displayNewRulesTable && (
            <FlexeButton
              id="config-spa-test-zone-rules-button"
              isDisabled={rules.length < 1}
              text="Test Zone Rules"
              handleClick={toggleTestRulesUploadModal}
            />
          )}
          <FileDownloadLink
            testId={'config-spa-table-download-button'}
            href={`/api/v2/putaway/warehouses/${cookies.selected_warehouse_id}/rules/export`}
            text={'Download CSV'}
            showAsButton
          />
          <FlexeButton
            id="bulk_import_button"
            level="primary"
            text={
              <>
                <i className="fa fa-upload"></i>
                Upload CSV
              </>
            }
            handleClick={toggleShowCsvUploadModal}
          />
        </div>
      </div>
      <div>
        {uploadComplete && (
          <CsvPostUploadStep responseErrors={responseErrors} genericErrors={genericErrors} csvErrors={csvErrors} />
        )}
      </div>
      {fetchingRules ? (
        <Loader loading={true} />
      ) : (
        <RulesTable
          warehouseId={cookies.selected_warehouse_id}
          pageSize={pageSize}
          rules={rules}
          totalRulesCount={totalRulesCount}
          currentPage={currentPage}
          handlePageChange={handlePagination}
          handlePageSizeChange={changePageSize}
          displayNewRulesTable={displayNewRulesTable}
        />
      )}
      <SpaConfigurationRulesBulkUploadModal
        id="bulk-container-create-modal"
        title={`Create SPA Rules From a CSV`}
        actionDescription={`create SPA rules`}
        show={showCsvUploadModal}
        templatePath={'/api/v2/putaway/warehouses/' + cookies.selected_warehouse_id + '/rules/csv_template'}
        toggleModal={toggleShowCsvUploadModal}
        instructionsTableRows={instructionTableHeaderRows}
        uploading={uploading}
        instructionsTableHeaders={instructionsTableHeaders}
        handleUpload={handleUpload}
        backwardsCompatible={false}
        requiredHeaders={requiredHeaders}
        fileUploadType={undefined}
      />
      <TestRulesModal
        warehouseId={cookies.selected_warehouse_id}
        id="test-putaway-rules"
        title={`Test Putaway Rules`}
        actionDescription={`create SPA rules`}
        show={showTestRulesUploadModal}
        toggleModal={toggleTestRulesUploadModal}
        uploading={uploading}
        spaConfigurationsRulesService={spaConfigRulesService}
      />
    </div>
  );
};

export default SpaConfigurationsIndex;
